import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ButtonBase, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ONLINE_CART_TOKENS } from './style';


export default function ProgressBar({ currentStep, onSectionClick, sections = [] }) {
  const mappedSections = useMemo(() => normalizeSections(sections), [sections]);
  const scrollRef = useRef(null);
  const sectionRefs = useRef([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const updateArrows = () => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const maxScrollLeft = scrollWidth - clientWidth;

    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(maxScrollLeft - scrollLeft > 4);
  };

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => {
      window.removeEventListener('resize', updateArrows);
    };
  }, [sections]);

  useEffect(() => {
    // handle auto scroll behaviour
    const container = scrollRef.current;
    if (!container || mappedSections.length === 0) {
      return;
    }

    const activeIndex = mappedSections.findIndex((section) => getSectionStatus(section, currentStep) === 'active');
    if (activeIndex < 0) {
      return;
    }

    const activeElement = sectionRefs.current[activeIndex];
    if (!activeElement) {
      return;
    }

    const containerLeft = container.scrollLeft;
    const containerRight = containerLeft + container.clientWidth;
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const elementLeft = activeElement.offsetLeft;
    const elementRight = elementLeft + activeElement.offsetWidth;
    const edgePadding = container.clientWidth < 600 ? 52 : 12;
    const isOutOfViewLeft = elementLeft < containerLeft + edgePadding;
    const isOutOfViewRight = elementRight > containerRight - edgePadding;

    if (activeIndex === 0 && container.scrollLeft !== 0) {
      container.scrollTo({
        left: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
      return;
    }

    if (isOutOfViewLeft || isOutOfViewRight) {
      const alignedLeft = Math.max(0, elementLeft - edgePadding);
      container.scrollTo({
        left: Math.min(maxScrollLeft, alignedLeft),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }
  }, [currentStep, mappedSections, prefersReducedMotion]);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, mappedSections.length);
  }, [mappedSections.length]);

  const scrollByAmount = (direction) => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const amount = Math.max(220, element.clientWidth * 0.7);
    element.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: ONLINE_CART_TOKENS.surface,
        borderTop: `1px solid ${ONLINE_CART_TOKENS.progressTopBorder}`,
      }}
    >
      <Box
        sx={{
          maxWidth: '900px',
          mx: 'auto',
          px: { xs: 2, sm: 2 },
          py: 1,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
        }}
      >
        <IconButton
          size="small"
          onClick={() => scrollByAmount('left')}
          disabled={!canScrollLeft}
          sx={{
            visibility: canScrollLeft ? 'visible' : 'hidden',
            width: 28,
            height: 28,
            border: `1px solid ${ONLINE_CART_TOKENS.borderSubtle}`,
            bgcolor: ONLINE_CART_TOKENS.surface,
            '&.Mui-disabled': {
              opacity: 0,
            },
          }}
          aria-label="Scroll sections left"
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box
          ref={scrollRef}
          onScroll={updateArrows}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            pl: { xs: 0.5, sm: 0 },
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {mappedSections.map((section, idx) => {
            const status = getSectionStatus(section, currentStep);
            const isClickable = status === 'completed';
            const minStep = Math.min(...section.steps);
            const progressWidth = status === 'completed' ? 100 : status === 'active' ? getActiveProgress(section, currentStep) : 0;
            const SectionTrigger = isClickable ? ButtonBase : Box;

            return (
              <Box
                key={section.label}
                ref={(element) => {
                  sectionRefs.current[idx] = element;
                }}
                sx={{ display: 'flex', alignItems: 'center', minWidth: { xs: 230, sm: 250 }, flex: { sm: 1 } }}
              >
                <SectionTrigger
                  onClick={isClickable ? () => onSectionClick?.(minStep) : undefined}
                  aria-label={isClickable ? `Go to section ${idx + 1}: ${section.label}` : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '4px',
                    gap: 1,
                    flex: 1,
                    minWidth: 0,
                    cursor: isClickable ? 'pointer' : 'default',
                    borderRadius: 1,
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    transition: 'all 0.2s ease',
                    '&:hover .section-text': {
                      textDecoration: isClickable ? 'underline' : 'none',
                    },
                    ...(isClickable
                      ? {
                          '&:focus-visible': {
                            outline: `2px solid ${ONLINE_CART_TOKENS.ring}`,
                            outlineOffset: 2,
                            '& .section-text': {
                              textDecoration: isClickable ? 'underline' : 'none',
                            },
                          },
                        }
                      : {}),
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      color: status === 'completed' || status === 'active' ? ONLINE_CART_TOKENS.surface : ONLINE_CART_TOKENS.sectionUpcomingText,
                      backgroundColor:
                        status === 'completed' ? ONLINE_CART_TOKENS.success : status === 'active' ? ONLINE_CART_TOKENS.primary : ONLINE_CART_TOKENS.sectionUpcomingBg,
                      ...(isClickable
                        ? {
                            '&:hover': {
                              boxShadow: `0 0 0 3px ${ONLINE_CART_TOKENS.successFocusRing}`,
                            },
                          }
                        : {}),
                    }}
                  >
                    {status === 'completed' ? <CheckIcon sx={{ fontSize: 14 }} /> : idx + 1}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      className="section-text"
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: status === 'completed' ? ONLINE_CART_TOKENS.textSuccess : status === 'active' ? ONLINE_CART_TOKENS.textHeadingSoft : ONLINE_CART_TOKENS.sectionUpcomingText,
                        fontFamily: ONLINE_CART_TOKENS.fontFamily,
                        transition: 'color 0.2s ease',
                        whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {`Section ${idx + 1}/${mappedSections.length}: `}
                      </Box>
                      {section.label}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.75,
                        height: 4,
                        borderRadius: '999px',
                        backgroundColor: ONLINE_CART_TOKENS.progressTrack,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: '999px',
                          width: `${Math.max(0, Math.min(100, progressWidth))}%`,
                          backgroundColor: status === 'completed' ? ONLINE_CART_TOKENS.success : ONLINE_CART_TOKENS.primary,
                          transition: 'width 0.7s ease-out',
                        }}
                      />
                    </Box>
                  </Box>
                </SectionTrigger>
                {idx < mappedSections.length - 1 && (
                  <Box
                    sx={{
                      width: { xs: 16, sm: 20 },
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <IconButton
          size="small"
          onClick={() => scrollByAmount('right')}
          disabled={!canScrollRight}
          sx={{
            visibility: canScrollRight ? 'visible' : 'hidden',
            width: 28,
            height: 28,
            border: `1px solid ${ONLINE_CART_TOKENS.borderSubtle}`,
            bgcolor: ONLINE_CART_TOKENS.surface,
            '&.Mui-disabled': {
              opacity: 0,
            },
          }}
          aria-label="Scroll sections right"
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function getSectionStatus(section, currentStep) {
  const maxStep = Math.max(...section.steps);
  const minStep = Math.min(...section.steps);

  if (currentStep > maxStep) {
    return 'completed';
  }
  if (currentStep >= minStep && currentStep <= maxStep) {
    return 'active';
  }
  return 'upcoming';
}

function getActiveProgress(section, currentStep) {
  const minStep = Math.min(...section.steps);
  return ((currentStep - minStep) / section.steps.length) * 100;
}

function normalizeSections(sections = []) {
  let stepCursor = 1;

  return sections.map((section) => {
    const stepCount = section.stepCount || 0;
    const steps = Array.from({ length: stepCount }, (_, index) => stepCursor + index);
    stepCursor += stepCount;

    return {
      label: section.label,
      steps,
    };
  });
}
